import React, { useState, useMemo } from 'react';
import TableIndex from '@shared/table/TableIndex';
import type { SupportRule, TableAction } from '@shared/table/TableIndex';
import ListPageHeader from '@shared/ListPageHeader';
import { Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { fulfillmentService } from '@services/operator/fulfillmentService';
import { MultipleValue } from '@shared/table/Rules/component';
import { formatDateTime } from '@utils/dateTime';
import { useMerchant } from '@contexts/operator/MerchantContext';
import { useIsStandard } from '@contexts/operator/PlanContext';
import GeneratePickingListModal from './components/GeneratePickingListModal';

interface FulfillmentRow {
  id: string;
  number?: string;
  name?: string;
  status?: string;
  created_at?: string;
  work_orders?: Array<{ id: string; barcode: string }>;
  [key: string]: unknown;
}

interface MerchantOption {
  id: string;
  name: string;
}

const MaterialPickingList = () => {
  const { t } = useTranslation();
  const [lastModified, setLastModified] = useState(0);
  const { merchants } = useMerchant() as { merchants: MerchantOption[] };
  const isStandard = useIsStandard();

  const [generateModalVisible, setGenerateModalVisible] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const columns = useMemo(() => [
    {
      nameKey: 'common.id',
      sortable: true,
      sortFieldName: 'work_orders.id',
      cell: ({ id, number, name }: FulfillmentRow) => (
        <Link to={`/fulfillments/${id}`}>
            <span style={{ fontWeight: 'bold' }}>{number}</span>
            {name && <div style={{ color: '#888', fontSize: '0.85em' }}>{name}</div>}
        </Link>
      )
    },
    {
        nameKey: 'operator.work_orders.list.title',
        cell: ({ work_orders }: FulfillmentRow) => (
            <div>
                {work_orders && work_orders.map(wo => (
                    <div key={wo.id}>
                        <Link to={`/work_orders/${wo.id}`}>{wo.barcode}</Link>
                    </div>
                ))}
            </div>
        )
    },
    {
      nameKey: 'operator.work_orders.list.columns.status',
      width: '120px',
      cell: ({ status }: FulfillmentRow) => {
        let color = 'default';
        if (status === 'allocated') color = 'blue';
        if (status === 'processing') color = 'orange';
        if (status === 'shipped' || status === 'completed') color = 'success';
        return <Tag color={color}>{t(`models.fulfillment.statuses.${status}`)}</Tag>;
      }
    },
    {
        nameKey: 'operator.work_orders.list.columns.created_at',
        sortable: true,
        sortFieldName: 'work_orders.created_at',
        cell: ({ created_at }: FulfillmentRow) => created_at ? formatDateTime(created_at) : '-'
    }
  ], [t]);

  const supportRules = useMemo<SupportRule[]>(() => (
     // 單一 merchant 模式下不顯示 merchant 篩選器
     isStandard ? [] : [
       {
          label: t('operator.material_picking.list.filter_tooltips.merchant.title'),
          component: MultipleValue,
          inputName: 'fulfillments.merchant_id',
          defaultOptions: merchants.map((m: MerchantOption) => ({ label: m.name, value: m.id })),
          createable: false,
          tooltip: { content: t('operator.material_picking.list.filter_tooltips.merchant.content') }
       }
     ]
  ), [t, merchants, isStandard]);

  const actions = useMemo<TableAction[]>(() => [
      {
          name: t('operator.menu.picking_lists.generate'),
          type: 'primary',
          do: (selected: any) => {
              if (selected.size === 0) return;
              setSelectedIds(Array.from(selected) as string[]);
              setGenerateModalVisible(true);
          }
      }
  ], [t]);

  return (
    <div>
      <ListPageHeader items={['production', 'production.material_picking']} />
      <TableIndex
        columns={columns}
        searchable={{
          match: 'prefix',
          searchFields: [
            t('models.fulfillment.number'),
            t('models.fulfillment.name'),
            t('models.order.name'),
            t('models.order.receiver_name'),
          ],
        }}
        tableId="MaterialPickingList"
        defaultQuery={{
            sorting_field: 'fulfillments.created_at',
            sorting_direction: 'desc',
            'fulfillments.source_type': 'WorkOrderItem'
        }}
        supportRules={supportRules}
        lastModified={lastModified}
        setLastModified={setLastModified}
        service={fulfillmentService as any}
        actions={actions}
      />
      <GeneratePickingListModal
          open={generateModalVisible}
          onClose={() => setGenerateModalVisible(false)}
          selectedIds={selectedIds}
          onSuccess={() => setLastModified(Date.now())}
      />
    </div>
  );
};

export default MaterialPickingList;
